import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { toast } from "sonner";
import { CheckCircle2, AlertTriangle, Clock, ChevronDown } from "lucide-react";

function statusDot(audit) {
  if (audit?.acknowledged) return { color: "text-emerald-600", label: "Acknowledged" };
  const completedAt = audit?.completed_at;
  if (completedAt) {
    try {
      const ageHrs = (Date.now() - new Date(completedAt).getTime()) / 3600000;
      if (ageHrs > 48) return { color: "text-rose-600", label: "Overdue" };
    } catch (e) { /* ignore parse errors */ }
  }
  return { color: "text-amber-500", label: "Pending" };
}

function AuditCard({ chart, onAck }) {
  const [open, setOpen] = useState(false);
  const [agreed, setAgreed] = useState(false);
  const [busy, setBusy] = useState(false);
  const audit = chart.audit || {};
  const dot = statusDot(audit);
  const acked = audit.acknowledged;

  const ack = async () => {
    setBusy(true);
    try {
      await api.post(`/charts/${chart.id}/acknowledge`);
      toast.success(`Audit on ${chart.chart_id} acknowledged`);
      onAck();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="border border-slate-200 rounded-xl bg-white" data-testid={`ack-card-${chart.chart_id}`}>
      <button type="button" onClick={() => setOpen(!open)}
              className="w-full flex flex-wrap items-center justify-between gap-3 px-4 py-3 hover:bg-slate-50">
        <div className="flex items-center gap-3">
          <span className={`inline-flex items-center gap-1 text-xs font-medium ${dot.color}`}>
            {acked ? <CheckCircle2 size={14}/> : dot.label === "Overdue" ? <AlertTriangle size={14}/> : <Clock size={14}/>}
            {dot.label}
          </span>
          <span className="font-mono text-sm">{chart.chart_id}</span>
          <span className="text-sm text-slate-700">{chart.patient_name}</span>
          <span className="text-xs uppercase tracking-wider bg-slate-100 text-slate-700 rounded px-2 py-0.5">
            {audit.audit_type === "oasis" ? "OASIS" : audit.audit_type === "combined" ? "Coding + OASIS" : "Coding"}
          </span>
        </div>
        <div className="flex items-center gap-3 text-sm">
          <span className="font-medium text-slate-800">{audit.score}%</span>
          <span className="text-slate-500">QA: {audit.qa_name}</span>
          <ChevronDown size={14} className={`transition-transform ${open ? "rotate-180" : ""}`}/>
        </div>
      </button>
      {open && (
        <div className="border-t border-slate-200 p-4 space-y-3 text-sm">
          <div className="grid sm:grid-cols-4 gap-3 text-xs">
            <div><div className="text-slate-500">Employee ID</div><div className="text-slate-800 font-mono">{chart.submitted_by}</div></div>
            <div><div className="text-slate-500">Coder</div><div className="text-slate-800">{chart.submitted_by_name}</div></div>
            <div><div className="text-slate-500">Client</div><div className="text-slate-800">{chart.client || "—"}</div></div>
            <div><div className="text-slate-500">Audit Date</div><div className="text-slate-800">{(audit.completed_at || "").slice(0, 10)}</div></div>
          </div>

          {(audit.coding_score != null || audit.oasis_score != null) && (
            <div className="flex flex-wrap gap-3 text-xs">
              {audit.coding_score != null && (
                <div className="bg-emerald-50 border border-emerald-200 rounded px-2.5 py-1.5"><span className="text-emerald-700 font-medium">Coding: </span><span className="font-mono">{audit.coding_score}%</span></div>
              )}
              {audit.oasis_score != null && (
                <div className="bg-indigo-50 border border-indigo-200 rounded px-2.5 py-1.5"><span className="text-indigo-700 font-medium">OASIS: </span><span className="font-mono">{audit.oasis_score}%</span></div>
              )}
            </div>
          )}

          {(() => {
            const all = audit.errors || [];
            const coding = audit.coding_errors || all.filter((e) => e.section === "coding");
            const oasis = audit.oasis_errors || all.filter((e) => e.section === "oasis");
            const untagged = all.filter((e) => !e.section && !coding.includes(e) && !oasis.includes(e));
            const renderBlock = (label, list, accentBg) => list.length > 0 && (
              <div data-testid={`ack-${chart.chart_id}-${label.toLowerCase()}-errors`}>
                <div className={`text-xs uppercase tracking-wider mb-1 ${accentBg}`}>{label} Errors ({list.length})</div>
                <div className="space-y-1">
                  {list.map((er, i) => (
                    <div key={i} className="flex flex-wrap items-start gap-2 text-xs border border-slate-200 rounded px-2 py-1.5 bg-slate-50/50" data-testid={`ack-error-${chart.chart_id}-${label.toLowerCase()}-${i}`}>
                      <span className="text-slate-700 font-medium">{er.category}</span>
                      <span className="text-slate-900">— {er.description}</span>
                      {er.comment && <div className="w-full pl-1 text-slate-600 italic">{er.comment}</div>}
                    </div>
                  ))}
                </div>
              </div>
            );
            return (
              <>
                {renderBlock("Coding", coding, "text-emerald-700")}
                {renderBlock("OASIS", oasis, "text-indigo-700")}
                {renderBlock("Other", untagged, "text-slate-600")}
              </>
            );
          })()}

          {audit.correct_codes && (
            <div>
              <div className="text-xs uppercase tracking-wider text-slate-500 mb-1">Correct ICD-10 Guidance</div>
              <div className="text-sm font-mono bg-emerald-50 border border-emerald-200 rounded p-2 whitespace-pre-wrap">{audit.correct_codes}</div>
            </div>
          )}
          {audit.correct_oasis_responses && (
            <div>
              <div className="text-xs uppercase tracking-wider text-slate-500 mb-1">Correct OASIS Guidance</div>
              <div className="text-sm bg-indigo-50 border border-indigo-200 rounded p-2 whitespace-pre-wrap">{audit.correct_oasis_responses}</div>
            </div>
          )}
          {audit.comments && (
            <div>
              <div className="text-xs uppercase tracking-wider text-slate-500 mb-1">QA Comments</div>
              <div className="text-sm bg-slate-50 border border-slate-200 rounded p-2 whitespace-pre-wrap">{audit.comments}</div>
            </div>
          )}

          {acked ? (
            <div className="bg-emerald-50 border border-emerald-200 rounded p-3 text-xs text-emerald-800">
              Acknowledged by {audit.acknowledged_by_name} on {(audit.acknowledged_at || "").slice(0, 19).replace("T", " ")}
            </div>
          ) : (
            <div className="bg-amber-50 border border-amber-200 rounded p-3">
              <label className="flex items-start gap-2 text-sm cursor-pointer" data-testid={`ack-checkbox-label-${chart.chart_id}`}>
                <input type="checkbox" checked={agreed} onChange={(e) => setAgreed(e.target.checked)}
                       data-testid={`ack-checkbox-${chart.chart_id}`}
                       className="mt-0.5 rounded border-slate-400"/>
                <span className="text-slate-800">
                  I have reviewed the audit findings, understood the identified errors, and will avoid repeating them in future charts.
                </span>
              </label>
              <div className="flex justify-end mt-3">
                <button type="button" disabled={!agreed || busy} onClick={ack}
                        data-testid={`ack-submit-${chart.chart_id}`}
                        className="inline-flex items-center gap-2 bg-emerald-700 text-white rounded-lg px-4 py-2 text-sm font-medium hover:bg-emerald-600 disabled:opacity-50">
                  <CheckCircle2 size={14}/> {busy ? "Acknowledging…" : "Acknowledge"}
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function Acknowledgements() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    api.get("/audits/needing-ack")
      .then((r) => setItems(r.data))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const pending = items.filter((c) => !c.audit?.acknowledged).length;

  return (
    <div className="p-6 md:p-10 max-w-5xl mx-auto" data-testid="acknowledgements-page">
      <PageHeader
        title="Audit Acknowledgements"
        subtitle="Every audit scoring less than 100% must be acknowledged. Open each card to review the findings and confirm understanding."
      />

      <div className="bg-white border border-slate-200 rounded-xl p-4 mb-4 flex items-center justify-between">
        <div className="text-sm text-slate-700">
          <span className="font-medium" data-testid="ack-pending-count">{pending}</span> pending acknowledgement{pending === 1 ? "" : "s"} · {items.length - pending} acknowledged
        </div>
        {pending > 0 && (
          <span className="inline-flex items-center gap-1 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-3 py-1">
            <AlertTriangle size={12}/> New work is paused for any audit beyond 48h without acknowledgement.
          </span>
        )}
      </div>

      {loading ? (
        <div className="bg-white border border-slate-200 rounded-xl p-8 text-center text-slate-500 text-sm">Loading…</div>
      ) : items.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-xl p-8 text-center text-slate-500 text-sm">
          🎉 No pending acknowledgements — all your audits scored 100%, or you&apos;ve reviewed every one.
        </div>
      ) : (
        <div className="space-y-2">
          {items.map((c) => <AuditCard key={c.id} chart={c} onAck={load} />)}
        </div>
      )}
    </div>
  );
}
